import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import TrustBar from "@/pages/home/components/TrustBar";
import ProcessSection from "@/pages/home/components/ProcessSection";
import ServiceAreas from "@/pages/home/components/ServiceAreas";
import Testimonials from "@/pages/home/components/Testimonials";
import FinalCTA from "@/pages/home/components/FinalCTA";
import MidPageCTA from "@/pages/home/components/MidPageCTA";
import { Link } from "react-router-dom";
import { useState } from "react";

const INCLUDED = [
  { icon: "ri-tools-line", title: "Full Mechanical Inspection", desc: "Engine, transmission, brakes, suspension, steering, and drivetrain — every moving part checked and documented with precision." },
  { icon: "ri-flashlight-line", title: "Complete Electrical System", desc: "Battery, charging, all electronics, lights, A/C, heat, and onboard diagnostics reviewed with professional diagnostic equipment." },
  { icon: "ri-car-line", title: "Body, Paint & Interior", desc: "We inspect for prior accidents, body repairs, rust, paint inconsistencies, and overall condition — inside and out." },
  { icon: "ri-road-map-line", title: "Test Drive", desc: "Every vehicle is test driven to evaluate real-world performance under acceleration, braking, and turning conditions." },
  { icon: "ri-compass-discover-line", title: "Undercarriage Inspection", desc: "We lift the vehicle and physically inspect the frame, suspension components, exhaust, and underside for damage or corrosion." },
  { icon: "ri-spy-line", title: "Fraud & Damage Detection", desc: "We check for odometer tampering, flood damage indicators, hidden accident damage, and title issues that CARFAX typically misses." },
  { icon: "ri-file-text-line", title: "Detailed Report + Photos", desc: "A comprehensive written inspection report with photographs emailed to you after every job — keep it or use it to negotiate." },
  { icon: "ri-history-line", title: "Free Title & History Report", desc: "Included with every inspection at no extra charge, along with a complimentary book value appraisal of the vehicle." },
];

const SERVICE_QA = [
  {
    q: "What is a pre-purchase used car inspection?",
    a: "A pre-purchase used car inspection is a comprehensive evaluation of a vehicle performed by an independent specialist before you buy it. Lemon Protector's 450-point inspection covers every major system — mechanical, electrical, body, chassis, and more — giving you a complete, unbiased picture of the vehicle's true condition.",
  },
  {
    q: "Why do I need a pre-purchase inspection?",
    a: "Sellers present vehicles in the best possible light. CARFAX only shows officially reported history. Without an independent inspection, you're relying on incomplete information. A 450-point inspection by Lemon Protector uncovers hidden problems, prior damage, and mechanical issues before you commit to the purchase.",
  },
  {
    q: "What are the benefits of a 450-point inspection?",
    a: "You get complete mechanical and physical data on the vehicle. You can negotiate from a position of knowledge. You avoid costly surprises after purchase. You can walk away from a bad deal with confidence. And you get to speak directly with the inspector who evaluated the car — a feature no other service in the area offers.",
  },
];

const SERVICE_FAQ = [
  { q: "Who offers pre-purchase car inspections near me?", a: "Lemon Protector provides professional pre-purchase car inspections across New York, New Jersey, and Connecticut. We travel on-site to the vehicle's location. Starting at $139 with direct inspector consultation included in every job." },
  { q: "How much does a 450-point car inspection cost?", a: "Lemon Protector's 450-point pre-purchase inspection starts at $139. That includes the full inspection, a written report with photos, a free title search, vehicle history report, and a book value appraisal — all with no hidden fees." },
  { q: "How long does a pre-purchase inspection take?", a: "A typical 450-point on-site inspection takes one to two hours. The inspector calls you immediately after with findings, and the written report is emailed the same day." },
];

export default function PrePurchaseInspection() {
  const [activeQA, setActiveQA] = useState<number | null>(0);
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  return (
    <div className="bg-brand-black min-h-screen font-sans">
      <Navbar />
      <main>
        {/* 1. Hero */}
        <section className="relative w-full min-h-[680px] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 w-full h-full">
            <img
              src="https://public.readdy.ai/ai/img_res/af39dba3c6199e4819e8d3c44507bc79.jpg"
              alt="450-point pre-purchase used car inspection — Lemon Protector"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-black/95 via-brand-black/78 to-brand-black/40" />
            <div className="absolute inset-0 bg-gradient-to-b from-brand-black/40 via-transparent to-brand-black/82" />
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <div className="max-w-2xl">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-5 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                Core Service
              </p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-brand-white leading-tight mb-5">
                450-Point Pre-Purchase<br />
                <span className="text-brand-gold italic">Used Car Inspection</span>
              </h1>
              <p className="font-sans text-base md:text-lg text-brand-gray-light leading-relaxed max-w-xl mb-6">
                The most thorough pre-purchase inspection available. We travel on-site to the vehicle — anywhere in NY, NJ, or CT — and give you a direct call from your actual inspector.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <span className="font-serif text-2xl font-bold text-brand-gold">Starting at $139</span>
                <span className="w-px h-5 bg-brand-gold/30" />
                <span className="font-sans text-sm text-brand-gray-light">Free Title &amp; History Report Included</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-search-line" />
                  Book This Inspection
                </Link>
                <a
                  href="tel:5169833800"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-brand-gold/50 text-brand-gold font-sans font-medium text-sm rounded-full hover:bg-brand-gold/10 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  Talk to an Inspector
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* 2. Trust Bar */}
        <TrustBar />

        {/* 3. What's Included */}
        <section className="bg-brand-black py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center justify-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                What&apos;s Covered
                <span className="w-8 h-px bg-brand-gold" />
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-brand-white leading-tight mb-4">
                Everything Included in Your 450-Point Inspection
              </h2>
              <p className="font-sans text-base text-brand-gray max-w-xl mx-auto">
                We inspect the entire vehicle — not just what&apos;s visible. Every system, every surface, every concern — fully documented.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
              {INCLUDED.map((item, i) => (
                <div key={i} className="bg-brand-charcoal border border-brand-gold/10 rounded-2xl p-6 hover:border-brand-gold/35 lp-card-glow transition-colors">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-brand-gold/10 mb-4">
                    <i className={`${item.icon} text-brand-gold text-lg`} />
                  </div>
                  <h3 className="font-serif text-base font-semibold text-brand-white mb-2">{item.title}</h3>
                  <p className="font-sans text-sm text-brand-gray leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-12 flex justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-9 py-4 bg-brand-gold text-brand-black font-sans font-bold text-sm tracking-wide rounded-full hover:bg-brand-gold-light transition-all whitespace-nowrap cursor-pointer"
              >
                Schedule Your Inspection
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </section>

        {/* 4. AI Answer Block */}
        <section className="bg-brand-panel py-24 lg:py-32">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-16">
              <div className="lg:w-1/3">
                <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
                  <span className="w-8 h-px bg-brand-gold" />
                  Service Q&amp;A
                </p>
                <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-brand-white leading-tight mb-5">
                  About Pre-Purchase Inspections
                </h2>
                <p className="font-sans text-sm text-brand-gray leading-relaxed">
                  Common questions from used-car buyers before booking their first inspection.
                </p>
                <div className="mt-10 font-serif text-[110px] leading-none text-brand-gold/8 select-none">&ldquo;</div>
              </div>
              <div className="lg:w-2/3 flex flex-col gap-4">
                {SERVICE_QA.map((item, i) => (
                  <div
                    key={i}
                    className={`rounded-2xl border transition-all duration-300 overflow-hidden ${activeQA === i ? "border-brand-gold/40 bg-brand-charcoal" : "border-brand-gold/10 bg-brand-charcoal/50 hover:border-brand-gold/25"}`}
                  >
                    <button className="w-full text-left px-6 py-5 flex items-start justify-between gap-4 cursor-pointer" onClick={() => setActiveQA(activeQA === i ? null : i)}>
                      <h3 className="font-serif text-base text-brand-white font-medium leading-snug">{item.q}</h3>
                      <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                        <i className={`text-brand-gold text-lg ${activeQA === i ? "ri-subtract-line" : "ri-add-line"}`} />
                      </div>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${activeQA === i ? "max-h-64 pb-6" : "max-h-0"}`}>
                      <p className="px-6 font-sans text-sm text-brand-gray-light leading-relaxed">{item.a}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 5. Process */}
        <ProcessSection />

        {/* 6. Why Choose Us — Comparison */}
        <section className="bg-brand-charcoal py-24 lg:py-32">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-center mb-16">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center justify-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                How We Compare
                <span className="w-8 h-px bg-brand-gold" />
              </p>
              <h2 className="font-serif text-4xl lg:text-5xl font-semibold text-brand-white leading-tight">
                Lemon Protector vs. CARFAX vs. A Mechanic
              </h2>
              <p className="font-sans text-base text-brand-gray mt-4 max-w-xl mx-auto">
                Understanding the difference could save you thousands of dollars on your next used car purchase.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  title: "Lemon Protector",
                  highlight: true,
                  icon: "ri-shield-check-fill",
                  points: [
                    "Physical 450-point bumper-to-bumper inspection",
                    "Detects unreported accidents & flood damage",
                    "Full test drive performed",
                    "Direct call from your actual inspector",
                    "Written report with photos included",
                    "Starting at $139 — no hidden fees",
                    "Zero conflict of interest",
                  ],
                },
                {
                  title: "CARFAX Report",
                  highlight: false,
                  icon: "ri-file-damage-line",
                  points: [
                    "Shows only officially reported incidents",
                    "Cannot detect unreported accidents",
                    "No physical inspection of vehicle",
                    "No mechanical condition data",
                    "No test drive or hands-on evaluation",
                    "Often incomplete or out of date",
                    "Misses fraud, flood, and tampering",
                  ],
                },
                {
                  title: "A Mechanic or Repair Shop",
                  highlight: false,
                  icon: "ri-tools-line",
                  points: [
                    "Focused on repair work, not evaluation",
                    "Financial incentive to find repair work",
                    "May not do a full vehicle assessment",
                    "Rarely tests for fraud or tampering",
                    "No formal buyer-side inspection protocol",
                    "Not specialized in pre-purchase evaluations",
                    "Potential conflict of interest",
                  ],
                },
              ].map((col, i) => (
                <div
                  key={i}
                  className={`rounded-2xl p-7 border flex flex-col ${col.highlight ? "border-brand-gold/60 bg-brand-dark shadow-lg shadow-brand-gold/6" : "border-brand-gold/10 bg-brand-charcoal"}`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-9 h-9 flex items-center justify-center shrink-0">
                      <i className={`${col.icon} text-xl ${col.highlight ? "text-brand-gold" : "text-brand-gray"}`} />
                    </div>
                    <h3 className={`font-serif text-lg font-semibold ${col.highlight ? "text-brand-gold" : "text-brand-white"}`}>{col.title}</h3>
                    {col.highlight && (
                      <span className="ml-auto px-2.5 py-1 bg-brand-gold/15 border border-brand-gold/30 text-brand-gold font-sans text-xs rounded-full whitespace-nowrap">Best Choice</span>
                    )}
                  </div>
                  <ul className="flex flex-col gap-3">
                    {col.points.map((pt, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                          <i className={`text-sm ${col.highlight ? "ri-check-line text-brand-gold" : "ri-close-line text-brand-gray/60"}`} />
                        </div>
                        <span className={`font-sans text-sm leading-relaxed ${col.highlight ? "text-brand-gray-light" : "text-brand-gray"}`}>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Mid CTA */}
        <MidPageCTA />

        {/* 7. Service Areas */}
        <ServiceAreas />

        {/* 8. Testimonials */}
        <Testimonials />

        {/* 9. FAQ */}
        <section className="bg-brand-panel py-24 lg:py-32">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <div className="mb-14">
              <p className="font-sans text-xs tracking-widest uppercase text-brand-gold mb-4 flex items-center gap-3">
                <span className="w-8 h-px bg-brand-gold" />
                FAQ
              </p>
              <h2 className="font-serif text-4xl font-semibold text-brand-white leading-tight">
                Pre-Purchase Inspection Questions
              </h2>
            </div>
            <div className="flex flex-col">
              {SERVICE_FAQ.map((item, i) => (
                <div key={i} className={`border-b ${i === 0 ? "border-t border-brand-gold/15" : "border-brand-gold/15"}`}>
                  <button
                    className="w-full text-left py-6 flex items-start justify-between gap-6 cursor-pointer group"
                    onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                  >
                    <h4 className={`font-serif text-lg leading-snug transition-colors ${openFAQ === i ? "text-brand-gold" : "text-brand-white group-hover:text-brand-gold"}`}>
                      {item.q}
                    </h4>
                    <div className="w-7 h-7 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`text-xl text-brand-gold ${openFAQ === i ? "ri-subtract-line" : "ri-add-line"}`} />
                    </div>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${openFAQ === i ? "max-h-52 pb-6" : "max-h-0"}`}>
                    <p className="font-sans text-sm text-brand-gray-light leading-relaxed">{item.a}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 10. Final CTA */}
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
}
